import { createBrowserRouter, useLocation } from "react-router-dom";
import NotFound from "@/components/NotFound";
import SingleCaseStudy from "@/components/SingleCaseStudy";
import SingleOpenRoles from "@/components/SingleOpenRoles";
import Page from "@/components/Page";
import Posts from "@/components/Posts";
import SinglePost from "@/components/SinglePost";
import PageTransition from "@/components/templateParts/PageTransition";
import Layout from "@/Layout";
import paths from "./paths";
import { previewLoader, routerGuard } from "./routerGuard";
import { pageFromPath } from "./utils";
import CategoryPage from "@/components/CategoryPage";
import TitleManager from "@/TitleManager";

const { show_on_front, page_for_posts, page_on_front, permalink_structure } =
  window.__REACT_WORDPRESS__.routing;

const permalinkRoute = permalink_structure
  .replace("%postname%/", "")
  .replaceAll("%/", "/")
  .replaceAll("%", ":");

const postsPageRoute =
  show_on_front === "page" && page_for_posts
    ? {
        path: `/${page_for_posts}`,
        element: (
          <PageTransition>
            <Posts page={pageFromPath(paths.postsPage(page_for_posts))} slug={page_for_posts} />
          </PageTransition>
        ),
        id: "Posts",
        loader: (e) => routerGuard({ routeData: e }),
        errorElement: <NotFound />,
      }
    : null;

// Maps a post type's REST base to the same component that renders it once
// published, so preview and production can never render a post type
// differently.
const PREVIEW_COMPONENTS = {
  pages: Page,
  posts: SinglePost,
  "case-studies": SingleCaseStudy,
  "open-roles": SingleOpenRoles,
};
const PreviewComponent = PREVIEW_COMPONENTS[window.__NS_PREVIEW__?.rest_base] || NotFound;

const isPreviewUrl = (url) => {
  const searchParams = new URL(url, window.location.origin).searchParams;
  return window.__NS_PREVIEW__ && searchParams.get("preview") === "true";
};

const RootPage = () => {
  const location = useLocation();
  const previewUrl = new URL(`${location.pathname}${location.search}`, window.location.origin);

  if (isPreviewUrl(previewUrl)) {
    return <PreviewComponent />;
  }

  return <Page slug={page_on_front} isHomepage={true} showIntro={true} />;
};

// Native previews and the homepage share the "/" pathname. Select the draft
// only while WordPress's preview query is present; navigating to "/" or a
// homepage anchor must load the published homepage.
const rootRoute =
  show_on_front === "page" && page_on_front
    ? {
        path: "/",
        element: <RootPage />,
        id: "Home",
        loader: (e) =>
          isPreviewUrl(e.request.url)
            ? previewLoader()
            : routerGuard({ routeData: e, homePage: true }),
        errorElement: <NotFound />,
      }
    : null;

const routes = () => {
  return createBrowserRouter([
    {
      path: "/",
      element: (
        <>
          <TitleManager />
          <Layout />
        </>
      ),
      id: "Layout",
      children: [
        rootRoute,
        postsPageRoute,
        {
          path: "/404",
          element: <NotFound />,
          id: "NotFound",
        },
        {
          path: `${permalinkRoute}:slug`,
          element: <SinglePost newsSlug={page_for_posts} />,
          loader: (e) => routerGuard({ routeData: e, type: "posts" }),
          id: "SinglePost",
        },
        {
          path: "/case-studies/:slug",
          element: <SingleCaseStudy />,
          loader: (e) => routerGuard({ routeData: e, type: "case-studies" }),
          id: "SingleCaseStudies",
        },
        {
          path: "/open-roles/:slug",
          element: <SingleOpenRoles />,
          loader: (e) => routerGuard({ routeData: e, type: "open-roles" }),
          id: "SingleOpenRoles",
        },
        {
          path: "/:slug",
          element: <Page />,
          loader: (e) => routerGuard({ routeData: e, type: "pages" }),
          id: "Page",
        },
        {
          path: "/insights/:categorySlug",
          element: <CategoryPage />,
          id: "CategoryPage",
        },
        {
          path: "/insights/:categorySlug/:slug",
          element: <SinglePost newsSlug={page_for_posts} />,
          loader: (e) => routerGuard({ routeData: e, type: "posts" }),
          id: "SinglePostInsights",
        },
      ].filter((route) => route),
      errorElement: (
        <Layout>
          <NotFound />
        </Layout>
      ),
    },
  ]);
};

export default routes;
